import { ElNotification } from "element-plus";
import { createRouter, createWebHistory, type RouteLocationNormalized } from "vue-router";

import {
  ROUTE_NAMES as ADMIN_ROUTE_NAMES,
  routes as adminRoutes,
} from "@/modules/admin";
import {
  ROUTE_NAMES as AUDITOR_ROUTE_NAMES,
  routes as auditorRoutes,
} from "@/modules/auditor";
import {
  ROUTE_NAMES as AUTH_ROUTE_NAMES,
  routes as authRoutes,
} from "@/modules/auth";
import { routes as complaintsRoutes } from "@/modules/complaints";
import { routes as devicesRoutes } from "@/modules/devices";
import { routes as driversRoutes } from "@/modules/drivers";
import { routes as billingRoutes } from "@/modules/billing";
import { routes as logsRoutes } from "@/modules/logs";
import { routes as reportRoutes } from "@/modules/reports";
import { routes as trackingRoutes } from "@/modules/tracking";
import { routes as usersRoutes } from "@/modules/users";
import { routes as vehicleRoutes } from "@/modules/vehicles";
import { ability } from "@/services/ability";
import { Subject } from "@/services/ability";
import { useAuthStore } from "@/stores/auth";
import { PERMISSION_KEYS } from "@/utils/permission-keys";
import { hasPermission } from "@/utils/permissions";


const ROUTE_PERMISSION_MAP: Record<string, { parent: string; child?: string }> = {
  dashboard: { parent: PERMISSION_KEYS.dashboard.root },
  homepage: { parent: PERMISSION_KEYS.dashboard.root },

  logs: { parent: PERMISSION_KEYS.logs.root },
  "logs.list": { parent: PERMISSION_KEYS.logs.root },
  "logs.logs.driver.elog": {
    parent: PERMISSION_KEYS.logs.root,
    child: PERMISSION_KEYS.logs.driverDailyLog,
  },
  "logs.logs.driver.transaction": {
    parent: PERMISSION_KEYS.logs.root,
    child: PERMISSION_KEYS.logs.driverWeeklyLog,
  },
  violations: { parent: PERMISSION_KEYS.violations },

  tracking: { parent: PERMISSION_KEYS.dashboard.root },
  "tracking.list": { parent: PERMISSION_KEYS.dashboard.root },
  "tracking.location": {
    parent: PERMISSION_KEYS.dashboard.root,
    child: PERMISSION_KEYS.dashboard.trackingList,
  },

  apiKey: { parent: PERMISSION_KEYS.apiKeys.root },

  reports: { parent: PERMISSION_KEYS.reports.root },
  "reports.fmcsa": {
    parent: PERMISSION_KEYS.reports.root,
    child: PERMISSION_KEYS.reports.fmcsaList,
  },
  "reports.ifta": {
    parent: PERMISSION_KEYS.reports.root,
    child: PERMISSION_KEYS.reports.iftaList,
  },
  "reports.driver": {
    parent: PERMISSION_KEYS.reports.root,
    child: PERMISSION_KEYS.reports.driversDailyList,
  },
  "reports.update": {
    parent: PERMISSION_KEYS.reports.root,
    child: PERMISSION_KEYS.reports.iftaView,
  },
  "reports.state": {
    parent: PERMISSION_KEYS.reports.root,
    child: PERMISSION_KEYS.reports.iftaView,
  },

  dvir: { parent: PERMISSION_KEYS.dvirs.root },
  "dvir-create": { parent: PERMISSION_KEYS.dvirs.root, child: PERMISSION_KEYS.dvirs.edit },
  "dvir-update": { parent: PERMISSION_KEYS.dvirs.root, child: PERMISSION_KEYS.dvirs.edit },
  "dvir-view": { parent: PERMISSION_KEYS.dvirs.root, child: PERMISSION_KEYS.dvirs.list },

  drivers: { parent: PERMISSION_KEYS.drivers.root },
  "drivers.list": { parent: PERMISSION_KEYS.drivers.root },
  "drivers.create": { parent: PERMISSION_KEYS.drivers.root, child: PERMISSION_KEYS.drivers.create },
  "drivers.update": { parent: PERMISSION_KEYS.drivers.root, child: PERMISSION_KEYS.drivers.update },

  vehicles: { parent: PERMISSION_KEYS.vehicles.root },
  "vehicles.list": { parent: PERMISSION_KEYS.vehicles.root },
  "vehicles.create": { parent: PERMISSION_KEYS.vehicles.root, child: PERMISSION_KEYS.vehicles.create },
  "vehicles.update": { parent: PERMISSION_KEYS.vehicles.root, child: PERMISSION_KEYS.vehicles.update },

  users: { parent: PERMISSION_KEYS.users.root },
  "users.list": { parent: PERMISSION_KEYS.users.root },
  "users.update": { parent: PERMISSION_KEYS.users.root, child: PERMISSION_KEYS.users.edit },

  devices: { parent: PERMISSION_KEYS.eldDevice.list },
  "devices.list": { parent: PERMISSION_KEYS.eldDevice.list },
  "devices.update": { parent: PERMISSION_KEYS.eldDevice.list },
  profile: { parent: PERMISSION_KEYS.profiles },
  "profile.main": { parent: PERMISSION_KEYS.profiles },
  "profile.change-password": { parent: PERMISSION_KEYS.profiles },

  chat: { parent: PERMISSION_KEYS.alarms.root },
  "chat.user": { parent: PERMISSION_KEYS.alarms.root, child: PERMISSION_KEYS.alarms.list },

  "auditor.companies": { parent: PERMISSION_KEYS.companies.root, child: PERMISSION_KEYS.companies.list },
  "auditor.monitoring": { parent: PERMISSION_KEYS.monitorings },
  "auditor.users": { parent: PERMISSION_KEYS.users.root, child: PERMISSION_KEYS.users.list },

  "admin.companies": { parent: PERMISSION_KEYS.companies.root, child: PERMISSION_KEYS.companies.list },
  "admin.monitoring": { parent: PERMISSION_KEYS.monitorings },
  "admin.managers": { parent: PERMISSION_KEYS.users.root, child: PERMISSION_KEYS.users.list },
};

function resolveRoutePermission(to: RouteLocationNormalized) {
  for (let i = to.matched.length - 1; i >= 0; i--) {
    const matched = to.matched[i];
    const routeName = matched.name?.toString();
    if (!routeName) continue;

    const permission = ROUTE_PERMISSION_MAP[routeName];
    if (permission) {
      return permission;
    }
  }

  const routeName = to.name?.toString();
  if (!routeName) return null;
  return ROUTE_PERMISSION_MAP[routeName] ?? null;
}
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/:companyId(\\d+)?",
      name: "dashboard",
      component: () => import("@/layouts/Default.vue"),
      redirect: {
        name: "homepage",
      },
      meta: {
        requiresAuth: true,
        subject: Subject.MainDashboard,
      },
      children: [
        {
          name: "homepage",
          path: "",
          component: () => import("@/views/HomeView.vue"),
        },

        ...logsRoutes,
        ...billingRoutes,
        ...reportRoutes,
        ...complaintsRoutes,
        ...trackingRoutes,
        ...driversRoutes,
        ...vehicleRoutes,
        ...usersRoutes,
        ...devicesRoutes,
        {
          path: "resource",
          name: "resource",
          component: () => import("@/views/ResourcePage.vue"),
        },
        // {
        //   path: "corrections",
        //   name: "corrections",
        //   component: () => import("@/views/Corrections.vue"),
        //   meta: {
        //     breadcrumb: "Corrections",
        //   },
        // },
        {
          path: "driver-logs",
          name: "driverLogs",
          component: () => import("@/views/DriverLogs.vue"),
          meta: {
            breadcrumb: "Complaints",
          },
        },

        {
          path: "driver-log-view/:id",
          name: "driverLogView",
          component: () => import("@/views/DriverLogView.vue"),
          meta: {
            breadcrumb: "Driver Log View",
          },
        },
        {
          path: "driver-logs-mock",
          name: "driverLogsMock",
          component: () => import("@/views/DriverLogsMockPage.vue"),
          meta: {
            breadcrumb: "Driver Logs Mock",
          },
        },
        {
          path: "driver-logs-mock/:id",
          name: "driverLogsMockView",
          component: () => import("@/views/DriverLogsMockView.vue"),
          meta: {
            breadcrumb: "Driver Log Mock View",
          },
        },

        {
          path: "violations",
          name: "violations",
          component: () => import("@/views/Violations.vue"),
          meta: {
            breadcrumb: "Violations",
          },
        },


        {
          path: "dvir",
          name: "dvir",
          component: () => import("@/views/Dvir.vue"),
          meta: {
            breadcrumb: "DVIR",
          },
        },

        {
          path: "dvir-create",
          name: "dvir-create",
          component: () => import("@/components/DvirCreate.vue"),
          meta: {
            breadcrumb: "Dvir Create",
          },
        },

        {
          path: "dvir-update/:id?",
          name: "dvir-update",
          component: () => import("@/components/DvirUpdate.vue"),
          meta: {
            breadcrumb: "Dvir Update",
          },
        },

        {
          path: "dvir/view/:id",
          name: "dvir-view",
          component: () => import("@/components/DvirView.vue"),
          meta: {
            breadcrumb: "Dvir View",
          },
        },

        {
          path: "api-key",
          name: "apiKey",
          component: () => import("@/views/ApiKey.vue"),
          meta: {
            breadcrumb: "Api Key",
          },
        },
        {
          path: "profile",
          name: "profile",
          component: () => import("@/views/profile/ProfilePage.vue"),
          meta: {
            breadcrumb: "Profile",
          },
          children: [
            {
              path: "",
              name: "profile.main",
              component: () => import("@/views/profile/ProfileMain.vue"),
            },
            {
              path: "change-password",
              name: "profile.change-password",
              components: {
                default: () => import("@/views/profile/ProfileMain.vue"),
                modal: () =>
                  import("@/views/profile/ProfileChangePassword.vue"),
              },
            },
          ],
        },

        {
          path: "violation",
          name: "violation",
          component: () => import("@/views/violation/ViolationPage.vue"),
          meta: {
            breadcrumb: "Setting Violation",
          },
          children: [
            {
              path: "",
              name: "violation.main",
              component: () => import("@/views/violation/ViolationMain.vue"),
            },
            // {
            //   path: "change-password",
            //   name: "profile.change-password",
            //   components: {
            //     default: () => import("@/views/profile/ProfileMain.vue"),
            //     modal: () =>
            //       import("@/views/profile/ProfileChangePassword.vue"),
            //   },
            // },
          ],
        },

        {
          path: "notification",
          name: "notification",
          component: () => import("@/views/notification/NotificationPage.vue"),
          meta: {
            breadcrumb: "Notification",
          },
          children: [
            {
              path: "",
              name: "notification.main",
              component: () =>
                import("@/views/notification/NotificationMain.vue"),
            },
          ],
        },

        {
          path: "maintenance",
          name: "maintenance",
          component: () => import("@/views/maintenance/MaintenancePage.vue"),
          meta: {
            breadcrumb: "Maintenance",
          },
        },
        {
          path: "alarm",
          name: "chat",
          component: () => import("@/views/chat/ChatMain.vue"),
          meta: {
            breadcrumb: "Alarm",
          },
          children: [
            {
              path: "messages/:chatId",
              name: "chat.user",
              components: {
                default: () => import("@/views/chat/ChatMain.vue"),
                chat: () => import("@/views/chat/ChatSect.vue"),
              },
            },
          ],
        },
        {
          path: "settings",
          name: "settings",
          component: () => import("@/views/settings/SettingsPage.vue"),
          meta: {
            breadcrumb: "Settings",
          },
        },
      ],
    },
    ...authRoutes,
    ...auditorRoutes,
    ...adminRoutes,
    {
      path: "/access-denied",
      name: "accessDenied",
      component: () => import("@/views/AccessDeniedPage.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/NotFoundPage.vue"),
    },
  ],
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const isBillingRoute = to.matched.some(
    (route) => route.path === "billing" || route.name === "billing"
  );
  const hasTokenOnly = Boolean(authStore.accessToken);

  const authRequired = to.matched.some((route) => route.meta.requiresAuth);
  if (authRequired && !authStore.isLoggedIn) {
    if (isBillingRoute && hasTokenOnly) {
      next();
      return;
    }
    next({ name: AUTH_ROUTE_NAMES.Login, query: { to: to.path } });
  } else {
    const routeWithSubject = to.matched.find((route) => route.meta.subject);
    const isMainDashboard = to.matched.some(
      (route) => route.name === "dashboard"
    );
    const subject = routeWithSubject?.meta.subject;
    const routePermission = resolveRoutePermission(to);

    if (subject && ability.cannot("view", subject)) {
      if (isBillingRoute && hasTokenOnly) {
        next();
        return;
      }
      next({ name: "accessDenied" });
    } else if (
      routePermission &&
      !hasPermission(authStore.userPermissions, routePermission.parent, routePermission.child)
    ) {
      next({ name: "accessDenied" });
    } else if (!to.params.companyId && authStore.isAuditor && isMainDashboard) {
      ElNotification({
        title: "Please select company",
        type: "info",
      });
      next({
        name: AUDITOR_ROUTE_NAMES.Companies,
        query: {
          selectedTab: "companies",
        },
      });
    } else if (!to.params.companyId && authStore.isAdmin && isMainDashboard) {
      if (hasPermission(authStore.userPermissions, PERMISSION_KEYS.monitorings)) {
        next({ name: ADMIN_ROUTE_NAMES.Monitoring });
      } else if (
        hasPermission(
          authStore.userPermissions,
          PERMISSION_KEYS.users.root,
          PERMISSION_KEYS.users.list
        )
      ) {
        next({ name: ADMIN_ROUTE_NAMES.Managers });
      } else {
        next({ name: ADMIN_ROUTE_NAMES.Companies });
      }
    } else {
      next();
    }
  }
});

export default router;

